import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalAgent } from "../models/ExternalAgent";
import { FunctionParameters } from "./FunctionParameters";
import { useGetExternalAgent } from "../api/useGetExternalAgent";
import { useCreateExternalAgent } from "../api/useCreateExternalAgent";
import { useUpdateExternalAgent } from "../api/useUpdateExternalAgent";
import { ModelSelector } from "../../chat-management/components/ModelSelector";
import { useGetLlmInfo } from "@/features/admin-llm-management/api/useGetLlmInfo";

interface ExternalAgentModalProps {
  open: boolean;
  onClose: () => void;
  agentName?: string;
}

export function ExternalAgentModal({
  open,
  onClose,
  agentName,
}: ExternalAgentModalProps) {
  const isEdit = Boolean(agentName);
  const {
    externalAgent,
    getExternalAgent,
    isLoading: isLoadingAgent,
  } = useGetExternalAgent();
  const { createExternalAgent, isLoading: isCreating } =
    useCreateExternalAgent();
  const { updateExternalAgent, isLoading: isUpdating } =
    useUpdateExternalAgent();
  const { llmInfoList, getLlmInfo } = useGetLlmInfo();

  const [formData, setFormData] = useState<ExternalAgent>(
    () =>
      new ExternalAgent({
        agentName: "",
        baseUrl: "",
        type: "ExternalAgentConfiguration",
        llmName: "",
        agentFunctionName: "",
        agentFunctionDescription: "",
        systemPrompt: "",
        searchFunctionDescription: "",
        searchFunctionParameters: {
          properties: {},
          required: [],
          type: "object",
        },
        retrieveFunctionDescription: "",
        retrieveFunctionParameters: {
          properties: {},
          required: [],
          type: "object",
        },
      }),
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      getLlmInfo();
    }
    if (isEdit && agentName && open) {
      getExternalAgent(agentName);
    } else if (!isEdit && open) {
      setFormData(
        new ExternalAgent({
          agentName: "",
          baseUrl: "",
          llmName: "",
          agentFunctionName: "",
          agentFunctionDescription: "",
          systemPrompt: "",
          searchFunctionDescription: "",
          searchFunctionParameters: {
            properties: {},
            required: [],
            type: "object",
          },
          retrieveFunctionDescription: "",
          retrieveFunctionParameters: {
            properties: {},
            required: [],
            type: "object",
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, agentName, open]);

  useEffect(() => {
    if (externalAgent) {
      setFormData(externalAgent);
    }
  }, [externalAgent]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.agentName.trim()) {
      newErrors.agentName = "Agent name is required";
    }

    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = "Base URL is required";
    }

    if (!formData.agentFunctionName.trim()) {
      newErrors.agentFunctionName = "Agent function name is required";
    }

    if (!formData.agentFunctionDescription.trim()) {
      newErrors.agentFunctionDescription =
        "Agent function description is required";
    }

    if (!formData.systemPrompt.trim()) {
      newErrors.systemPrompt = "System prompt is required";
    }

    if (!formData.searchFunctionDescription.trim()) {
      newErrors.searchFunctionDescription =
        "Search function description is required";
    }

    if (!formData.retrieveFunctionDescription.trim()) {
      newErrors.retrieveFunctionDescription =
        "Retrieve function description is required";
    }

    if (
      Object.keys(formData.searchFunctionParameters.properties).length === 0
    ) {
      newErrors.searchFunctionParameters =
        "At least one search function parameter is required";
    }

    if (
      Object.keys(formData.retrieveFunctionParameters.properties).length === 0
    ) {
      newErrors.retrieveFunctionParameters =
        "At least one retrieve function parameter is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (isEdit) {
      await updateExternalAgent(formData);
    } else {
      await createExternalAgent(formData);
    }

    onClose();
  };

  const handleInputChange = (field: keyof ExternalAgent, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      return new ExternalAgent(newData);
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const isLoading = isLoadingAgent || isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit External Agent" : "Create External Agent"}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <form onSubmit={handleSubmit} className="space-y-6 pr-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="agentName">Agent Name *</Label>
                <Input
                  id="agentName"
                  value={formData.agentName}
                  onChange={(e) =>
                    handleInputChange("agentName", e.target.value)
                  }
                  placeholder="Unique identifier of your agent"
                  disabled={isEdit}
                  required
                />
                {errors.agentName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.agentName}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Unique identifier of your agent
                </p>
              </div>

              <div>
                <Label htmlFor="baseUrl">Base URL *</Label>
                <Input
                  id="baseUrl"
                  value={formData.baseUrl}
                  onChange={(e) => handleInputChange("baseUrl", e.target.value)}
                  placeholder="http://localhost:5295/"
                  required
                />
                {errors.baseUrl && (
                  <p className="text-red-500 text-sm mt-1">{errors.baseUrl}</p>
                )}
              </div>
            </div>

            {llmInfoList && (
              <div>
                <Label>LLM Model *</Label>
                <ModelSelector
                  selectedLlmName={formData.llmName}
                  onLlmChange={(llmName) =>
                    handleInputChange("llmName", llmName)
                  }
                  llmInfo={llmInfoList}
                />
              </div>
            )}

            <div>
              <Label htmlFor="agentFunctionName">Agent Function Name *</Label>
              <Input
                id="agentFunctionName"
                value={formData.agentFunctionName}
                onChange={(e) =>
                  handleInputChange("agentFunctionName", e.target.value)
                }
                placeholder="ask_about_company_products"
                required
              />
              {errors.agentFunctionName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.agentFunctionName}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Function that will be called by LLM in Primary chat.
              </p>
            </div>

            <div>
              <Label htmlFor="agentFunctionDescription">
                Agent Function Description *
              </Label>
              <Textarea
                id="agentFunctionDescription"
                value={formData.agentFunctionDescription}
                onChange={(e) =>
                  handleInputChange("agentFunctionDescription", e.target.value)
                }
                placeholder="This function allows you to ask about company products and get detailed information."
                rows={3}
                required
              />
              {errors.agentFunctionDescription && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.agentFunctionDescription}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Information that will help Primary chat to identify when agent
                should be called.
              </p>
            </div>

            <div>
              <Label htmlFor="systemPrompt">System Prompt *</Label>
              <Textarea
                id="systemPrompt"
                value={formData.systemPrompt}
                onChange={(e) =>
                  handleInputChange("systemPrompt", e.target.value)
                }
                placeholder="You are able to search through company products and get information about them."
                rows={4}
                required
              />
              {errors.systemPrompt && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.systemPrompt}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Prompt of the external agent that should contain information on
                which data it can search and retrieve. Add as much details on
                different data types and information that LLM can work with.
              </p>
            </div>

            <div>
              <Label htmlFor="searchFunctionDescription">
                Search Function Description *
              </Label>
              <Textarea
                id="searchFunctionDescription"
                value={formData.searchFunctionDescription}
                onChange={(e) =>
                  handleInputChange("searchFunctionDescription", e.target.value)
                }
                placeholder="This function retrieves information about company products based on the provided parameters."
                rows={3}
                required
              />
              {errors.searchFunctionDescription && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.searchFunctionDescription}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Basic information on how to use search function or how to use
                different parameters. You can include an example.
              </p>
            </div>

            <FunctionParameters
              title="Search Function Parameters"
              parameters={formData.searchFunctionParameters}
              onChange={(params) =>
                handleInputChange("searchFunctionParameters", params)
              }
              id="search-params"
            />
            {errors.searchFunctionParameters && (
              <p className="text-red-500 text-sm mt-1">
                {errors.searchFunctionParameters}
              </p>
            )}

            <div>
              <Label htmlFor="retrieveFunctionDescription">
                Retrieve Function Description *
              </Label>
              <Textarea
                id="retrieveFunctionDescription"
                value={formData.retrieveFunctionDescription}
                onChange={(e) =>
                  handleInputChange(
                    "retrieveFunctionDescription",
                    e.target.value,
                  )
                }
                placeholder="This function retrieves detailed information about a specific product using its unique identifier."
                rows={3}
                required
              />
              {errors.retrieveFunctionDescription && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.retrieveFunctionDescription}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Basic information on what will be returned by the retrieve
                request. You can include an example.
              </p>
            </div>

            <FunctionParameters
              title="Retrieve Function Parameters"
              parameters={formData.retrieveFunctionParameters}
              onChange={(params) =>
                handleInputChange("retrieveFunctionParameters", params)
              }
              id="retrieve-params"
            />
            {errors.retrieveFunctionParameters && (
              <p className="text-red-500 text-sm mt-1">
                {errors.retrieveFunctionParameters}
              </p>
            )}
          </form>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? "Saving..." : isEdit ? "Update Agent" : "Create Agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
